import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Zap } from 'lucide-react';
import type { PaymentMethod, UtilityBill, UtilityBillCategory, UtilityBillCycle } from '@itr/shared';
import {
  PAYMENT_METHODS,
  UTILITY_BILL_CATEGORIES,
  UTILITY_BILL_CATEGORY_LABELS,
  UTILITY_BILL_CYCLES,
  advanceUtilityBillDue,
  formatMoney,
  formatRelative,
  utilityBillUrgency,
} from '@itr/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Badge, Drawer, EmptyState, ErrorState, SkeletonTable, useFetch, useToast } from '../components/ui';

export type BillRow = UtilityBill & { customerName: string; siteName: string | null };

const URGENCY_LABEL: Record<ReturnType<typeof utilityBillUrgency>, string> = {
  overdue: 'overdue',
  due_soon: 'due soon',
  ok: 'ok',
  inactive: 'inactive',
};

const URGENCY_INTENT: Record<ReturnType<typeof utilityBillUrgency>, 'neutral' | 'good' | 'warn' | 'bad' | 'info'> = {
  overdue: 'bad',
  due_soon: 'warn',
  ok: 'good',
  inactive: 'neutral',
};

/** Global cross-client view of every vendor bill that could interrupt a call center if it lapses. */
export function UtilityBillsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const canWrite = can('utility:write');

  const [category, setCategory] = useState('');
  const [urgency, setUrgency] = useState<'' | 'overdue' | 'due_soon' | 'ok'>('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<BillRow | null>(null);
  const [paying, setPaying] = useState<BillRow | null>(null);

  const bills = useFetch(() => api.get<BillRow[]>('/api/utility-bills', { category: category || undefined }), [category]);
  const customers = useFetch(() => api.get<Array<{ id: string; name: string }>>('/api/customers'), []);

  const rows = (bills.data ?? []).filter((b) => !urgency || utilityBillUrgency(b) === urgency);

  const stats = useMemo(() => {
    const active = (bills.data ?? []).filter((b) => b.active);
    const overdue = active.filter((b) => utilityBillUrgency(b) === 'overdue').length;
    const dueSoon = active.filter((b) => utilityBillUrgency(b) === 'due_soon').length;
    return { overdue, dueSoon, total: active.length };
  }, [bills.data]);

  return (
    <>
      <div className="page-header">
        <div className="grow">
          <h1 className="page-title">Utility bills</h1>
          <div className="page-subtitle">Vendor services a client's site depends on to stay online — a lapse here can interrupt their call center.</div>
        </div>
        {canWrite && (
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            <Plus size={15} /> New bill
          </button>
        )}
      </div>

      <div className="stat-strip">
        <div className="stat-chip">
          <div className="stat-chip-label">Tracked bills</div>
          <div className="stat-chip-value">{stats.total}</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Due within their reminder window</div>
          <div className="stat-chip-value">{stats.dueSoon}</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Overdue</div>
          <div className="stat-chip-value" style={{ color: stats.overdue > 0 ? 'var(--itr-critical)' : undefined }}>
            {stats.overdue}
          </div>
        </div>
      </div>

      <div className="row" style={{ marginBottom: 8, gap: 4, flexWrap: 'wrap' }}>
        {(
          [
            ['', 'All'],
            ['overdue', 'Overdue'],
            ['due_soon', 'Due soon'],
            ['ok', 'OK'],
          ] as const
        ).map(([v, label]) => (
          <button key={v || 'all'} className={`btn btn-sm${urgency === v ? ' btn-primary' : ''}`} onClick={() => setUrgency(v)}>
            {label}
          </button>
        ))}
      </div>

      <div className="row" style={{ marginBottom: 12, gap: 4, flexWrap: 'wrap' }}>
        <button className={`btn btn-sm${category === '' ? ' btn-primary' : ''}`} onClick={() => setCategory('')}>
          All categories
        </button>
        {UTILITY_BILL_CATEGORIES.map((c) => (
          <button key={c} className={`btn btn-sm${category === c ? ' btn-primary' : ''}`} onClick={() => setCategory(c)}>
            {UTILITY_BILL_CATEGORY_LABELS[c]}
          </button>
        ))}
      </div>

      <div className="card">
        {bills.loading && !bills.data ? (
          <div style={{ padding: 16 }}>
            <SkeletonTable rows={5} cols={6} />
          </div>
        ) : bills.error ? (
          <ErrorState error={bills.error} onRetry={bills.reload} />
        ) : rows.length === 0 ? (
          <EmptyState icon={<Zap size={20} />} title="No utility bills" text="Add a client's internet, power or phone bill to get reminders before it's due." />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Category</th>
                  <th>Provider</th>
                  <th data-align="right" style={{ width: 110 }}>
                    Amount
                  </th>
                  <th style={{ width: 150 }}>Next due</th>
                  <th style={{ width: 90 }}>Cycle</th>
                  {canWrite && <th style={{ width: 150 }} />}
                </tr>
              </thead>
              <tbody>
                {rows.map((bill) => {
                  const u = utilityBillUrgency(bill);
                  return (
                    <tr key={bill.id}>
                      <td style={{ fontWeight: 550, cursor: 'pointer' }} onClick={() => navigate(`/clients/${bill.customerId}`)}>
                        {bill.customerName}
                        {bill.siteName && <span className="subtle" style={{ fontSize: 11.5, marginLeft: 6 }}>{bill.siteName}</span>}
                      </td>
                      <td>
                        {UTILITY_BILL_CATEGORY_LABELS[bill.category]}
                        {bill.label && <span className="subtle" style={{ fontSize: 11.5, marginLeft: 6 }}>{bill.label}</span>}
                      </td>
                      <td className="subtle">{bill.provider || '—'}</td>
                      <td data-align="right" className="nums">
                        {formatMoney(bill.amountMinor, bill.currency)}
                      </td>
                      <td>
                        <div className="row" style={{ gap: 6 }}>
                          <Badge intent={URGENCY_INTENT[u]}>{URGENCY_LABEL[u]}</Badge>
                          <span className="subtle" style={{ fontSize: 12 }}>
                            {formatRelative(bill.nextDueAt)}
                          </span>
                        </div>
                      </td>
                      <td className="subtle" style={{ fontSize: 12 }}>
                        {bill.cycle.replace('_', ' ')}
                      </td>
                      {canWrite && (
                        <td>
                          <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                            {bill.active && (
                              <button className="btn btn-sm" onClick={() => setPaying(bill)}>
                                Mark paid
                              </button>
                            )}
                            <button className="btn btn-sm" onClick={() => setEditing(bill)}>
                              Edit
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Drawer open={creating} onClose={() => setCreating(false)} title="New utility bill">
        <UtilityBillForm
          customers={customers.data ?? []}
          onSaved={() => {
            setCreating(false);
            bills.reload();
            toast('Utility bill added', 'good');
          }}
        />
      </Drawer>

      <Drawer open={Boolean(editing)} onClose={() => setEditing(null)} title={editing ? `Edit — ${editing.customerName}` : ''}>
        {editing && (
          <UtilityBillForm
            bill={editing}
            customers={customers.data ?? []}
            fixedCustomerId={editing.customerId}
            onSaved={() => {
              setEditing(null);
              bills.reload();
              toast('Utility bill updated', 'good');
            }}
          />
        )}
      </Drawer>

      <Drawer open={Boolean(paying)} onClose={() => setPaying(null)} title={paying ? `Mark paid — ${paying.customerName}` : ''}>
        {paying && (
          <MarkUtilityBillPaidForm
            bill={paying}
            onSaved={() => {
              setPaying(null);
              bills.reload();
              toast('Payment recorded', 'good');
            }}
          />
        )}
      </Drawer>
    </>
  );
}

/* ================================================================== */
/* Forms — shared with the utility bills card on a client's own page  */
/* ================================================================== */

export function UtilityBillForm({
  bill,
  customers,
  fixedCustomerId,
  onSaved,
}: {
  bill?: UtilityBill | null;
  customers: Array<{ id: string; name: string }>;
  /** Locks the customer field, for adding/editing a bill from that client's own page. */
  fixedCustomerId?: string;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [customerId, setCustomerId] = useState(fixedCustomerId ?? bill?.customerId ?? '');
  const [category, setCategory] = useState<UtilityBillCategory>(bill?.category ?? 'internet');
  const [label, setLabel] = useState(bill?.label ?? '');
  const [provider, setProvider] = useState(bill?.provider ?? '');
  const [accountNumber, setAccountNumber] = useState(bill?.accountNumber ?? '');
  const [amount, setAmount] = useState(bill ? String(bill.amountMinor / 100) : '');
  const [currency, setCurrency] = useState(bill?.currency ?? 'USD');
  const [cycle, setCycle] = useState<UtilityBillCycle>(bill?.cycle ?? 'monthly');
  const [nextDueAt, setNextDueAt] = useState(bill ? isoDate(bill.nextDueAt) : isoDate(Date.now()));
  const [remindDaysBefore, setRemindDaysBefore] = useState(String(bill?.remindDaysBefore ?? 5));
  const [notes, setNotes] = useState(bill?.notes ?? '');
  const [active, setActive] = useState(bill?.active ?? true);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const body = {
      customerId,
      category,
      label: label.trim(),
      provider: provider.trim(),
      accountNumber: accountNumber.trim() || null,
      amountMinor: Math.round((Number(amount) || 0) * 100),
      currency: currency.toUpperCase(),
      cycle,
      nextDueAt: Date.parse(nextDueAt),
      remindDaysBefore: Number(remindDaysBefore) || 0,
      notes: notes.trim() || null,
      active,
    };
    try {
      if (bill) await api.patch(`/api/utility-bills/${bill.id}`, body);
      else await api.post('/api/utility-bills', body);
      onSaved();
    } catch (err) {
      toast('Could not save the utility bill', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      {!fixedCustomerId && (
        <div className="field">
          <label className="field-label">Client</label>
          <select className="select" required value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            <option value="">Select…</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="row" style={{ gap: 8 }}>
        <div className="field grow">
          <label className="field-label">Category</label>
          <select className="select" value={category} onChange={(e) => setCategory(e.target.value as UtilityBillCategory)}>
            {UTILITY_BILL_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {UTILITY_BILL_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </div>
        <div className="field grow">
          <label className="field-label">Label</label>
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Fiber 100Mbps" />
        </div>
      </div>

      <div className="row" style={{ gap: 8 }}>
        <div className="field grow">
          <label className="field-label">Provider</label>
          <input className="input" value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="Vendor / carrier name" />
        </div>
        <div className="field grow">
          <label className="field-label">Account number</label>
          <input className="input" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
        </div>
      </div>

      <div className="row" style={{ gap: 8 }}>
        <div className="field grow">
          <label className="field-label">Amount</label>
          <input className="input" type="number" step="0.01" min="0" required value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div className="field" style={{ width: 90 }}>
          <label className="field-label">Currency</label>
          <input className="input" maxLength={3} value={currency} onChange={(e) => setCurrency(e.target.value)} />
        </div>
        <div className="field grow">
          <label className="field-label">Billed</label>
          <select className="select" value={cycle} onChange={(e) => setCycle(e.target.value as UtilityBillCycle)}>
            {UTILITY_BILL_CYCLES.map((c) => (
              <option key={c} value={c}>
                {c === 'one_time' ? 'One time' : c[0]!.toUpperCase() + c.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="row" style={{ gap: 8 }}>
        <div className="field grow">
          <label className="field-label">Next due date</label>
          <input className="input" type="date" required value={nextDueAt} onChange={(e) => setNextDueAt(e.target.value)} />
        </div>
        <div className="field grow">
          <label className="field-label">Remind (days before)</label>
          <input className="input" type="number" min={0} value={remindDaysBefore} onChange={(e) => setRemindDaysBefore(e.target.value)} />
        </div>
      </div>

      <div className="field">
        <label className="field-label">Notes</label>
        <textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Contract terms, who pays it, escalation contact…" />
      </div>

      {bill && (
        <label className="row" style={{ gap: 8, marginBottom: 16, cursor: 'pointer', fontSize: 13 }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active — tracked and eligible for reminders
        </label>
      )}

      <button className="btn btn-primary" type="submit" disabled={busy || !customerId} style={{ width: '100%' }}>
        {busy ? 'Saving…' : bill ? 'Save changes' : 'Add bill'}
      </button>
    </form>
  );
}

export function MarkUtilityBillPaidForm({ bill, onSaved }: { bill: UtilityBill; onSaved: () => void }) {
  const toast = useToast();
  const [amount, setAmount] = useState(String(bill.amountMinor / 100));
  const [method, setMethod] = useState<PaymentMethod>('bank_transfer');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const nextDue = advanceUtilityBillDue(bill.nextDueAt, bill.cycle);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amountMinor = Math.round(Number(amount) * 100);
    if (!Number.isFinite(amountMinor) || amountMinor <= 0) {
      toast('Enter a valid amount', 'bad');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/api/utility-bills/${bill.id}/pay`, {
        amountMinor,
        method,
        reference: reference.trim() || null,
        note: note.trim() || null,
      });
      onSaved();
    } catch (err) {
      toast('Could not record the payment', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <p className="subtle" style={{ fontSize: 13, marginBottom: 16 }}>
        {nextDue !== null
          ? `This is a ${bill.cycle} bill — paying it reschedules it to ${isoDate(nextDue)}.`
          : 'This is a one-time bill — paying it marks it inactive rather than rescheduling.'}
      </p>
      <div className="field">
        <label className="field-label">Amount ({bill.currency})</label>
        <input className="input" type="number" min={0.01} step={0.01} required autoFocus value={amount} onChange={(e) => setAmount(e.target.value)} />
      </div>
      <div className="field">
        <label className="field-label">Method</label>
        <select className="select" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
          {PAYMENT_METHODS.map((m) => (
            <option key={m} value={m}>
              {m.replace('_', ' ')}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label className="field-label">Reference</label>
        <input className="input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Receipt/transaction number" />
      </div>
      <div className="field">
        <label className="field-label">Note</label>
        <textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Recording…' : 'Record payment'}
      </button>
    </form>
  );
}

function isoDate(ts: number): string {
  return new Date(ts).toISOString().slice(0, 10);
}
